import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Box, Typography, Card, CardContent, Pagination, CircularProgress } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import FormProvider from 'src/components/hook-form';
import { endpoints } from 'src/utils/axios';
import { getRequest, postRequest } from 'src/services';
import { useProfileContext } from 'src/components/profile/context';
import PayrollEmployeeCard from './genrate-payroll-create-emp-card';
import PayrollHeader from './genrate-payroll-create-page-header';
import axios from 'axios';
import { formatDate } from '../../risk-register/feching';

const GeneratePayrollCreatePage = () => {
  const { userProfile } = useProfileContext();
  const navigate = useNavigate();
  const [weekEndingsDate, setWeekEndingsDate] = useState(null);
  const [rawPayrollData, setRawPayrollData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [staticPayrollNumber, setStaticPayrollNumber] = useState('');
  const [netWeeklyPaid, setNetWeeklyPaid] = useState(null); // Store netWeeklyPaid here
  const [totalDeductions, setTotalDeductions] = useState(0);
  console.log('🚀 >>>>>>>>>>>>>> totalDeductions:', totalDeductions);

  const [employeeTaxData, setEmployeeTaxData] = useState(null);
  const [loadingTaxData, setLoadingTaxData] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [bankInformation, setBankInformation] = useState([]);
  const [employeeTaxApiProcessed, setEmployeeTaxApiProcessed] = useState({});
  const [saveTrigger, setSaveTrigger] = useState(false);

  useEffect(() => {
    fetchEmployees();
    getBankInformation();
    fetchPayrollListForNumbering();
  }, []);

  const fetchEmployees = async () => {
    const res = await getRequest(endpoints.accounting.employee.get_list);
    if (res) setEmployees(res);
  };

  const getBankInformation = async () => {
    const data = await getRequest(endpoints.accounting.bank_information.get_list);
    if (data) {
      setBankInformation(data);
    }
  };
  const fetchPayrollListForNumbering = async () => {
    try {
      const res = await getRequest(endpoints.accounting.payroll.generate_payroll.get_employee_list);
      if (res) {
        const nextPayroll = generateNextPayrollNumber(res);
        setStaticPayrollNumber(nextPayroll);
      }
    } catch (err) {
      console.error('Error fetching payroll list for number generation:', err);
    }
  };

  const generateNextPayrollNumber = (payrolls = []) => {
    const numbers = payrolls.map((p) => parseInt(p.payroll_number)).filter((n) => !isNaN(n));

    const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    return String(nextNumber).padStart(3, '0');
  };

  const validationSchema = Yup.object().shape({
    week_endings: Yup.date().nullable().required('Week ending date is required'),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      week_endings: null,
    },
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const companyInfo = useMemo(
    () => [
      { header: 'Name Of Contractor / SubContractor', value: userProfile?.company_name || '' },
      { header: 'Payroll Number', value: staticPayrollNumber || '' },
      { header: 'Address', value: userProfile?.address || userProfile?.city || '' },
    ],
    [userProfile, staticPayrollNumber]
  );

  const calculateHoursFromTimesheet = useCallback((employee) => {
    if (!employee || !employee.time_sheet || !employee.time_sheet.weekly_time_sheet) {
      return {
        standardHours: getDefaultHoursObject(),
        overtimeHours: getDefaultHoursObject(),
      };
    }

    const timeSheet = employee.time_sheet;
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const standardHours = getDefaultHoursObject();
    const overtimeHours = getDefaultHoursObject();
    let hourlyRate = parseFloat(employee.hourly_rate || 0).toFixed(2);
    let overtimeRate = (parseFloat(hourlyRate) * 0).toFixed(2);

    // Try to get rates from trade_worker_title if available
    if (timeSheet.weekly_time_sheet.some((day) => day.entries && day.entries.length > 0)) {
      for (const day of timeSheet.weekly_time_sheet) {
        if (day.entries && day.entries.length > 0 && day.entries[0].trade_worker_title) {
          hourlyRate = parseFloat(
            day.entries[0].trade_worker_title.regular_time_rate || hourlyRate
          ).toFixed(2);
          overtimeRate = parseFloat(
            day.entries[0].trade_worker_title.over_time_rate || overtimeRate
          ).toFixed(2);
          break;
        }
      }
    }

    timeSheet.weekly_time_sheet.forEach((dayData, index) => {
      const dayKey = days[index].toLowerCase();
      const dayHours = parseFloat(timeSheet.sub_totals[`total${days[index]}Hours`] || 0);

      standardHours[dayKey] = Math.min(8, dayHours).toFixed(2);
      overtimeHours[dayKey] = Math.max(0, dayHours - 8).toFixed(2);
    });

    let standardTotal = 0;
    let overtimeTotal = 0;

    Object.keys(standardHours)
      .filter((key) => !['totalHours', 'payRate', 'totalEarned'].includes(key))
      .forEach((key) => {
        standardTotal += parseFloat(standardHours[key]);
        overtimeTotal += parseFloat(overtimeHours[key]);
      });

    standardHours.totalHours = standardTotal.toFixed(2);
    overtimeHours.totalHours = overtimeTotal.toFixed(2);
    standardHours.payRate = hourlyRate;
    overtimeHours.payRate = overtimeRate;

    // Calculate total earned by multiplying hours by pay rate
    standardHours.totalEarned = (standardTotal * parseFloat(hourlyRate)).toFixed(2);
    overtimeHours.totalEarned = (overtimeTotal * parseFloat(overtimeRate)).toFixed(2);

    return { standardHours, overtimeHours };
  }, []);

  const getDefaultHoursObject = useCallback(
    () => ({
      sunday: '0.00',
      monday: '0.00',
      tuesday: '0.00',
      wednesday: '0.00',
      thursday: '0.00',
      friday: '0.00',
      saturday: '0.00',
      totalHours: '0.00',
      payRate: '0.00',
      totalEarned: '0.00',
    }),
    []
  );

  const getEmployeeTaxDetails = useCallback(
    (employee, grossAmount) => {
      const taxDetail = employeeTaxData?.['2025']?.tax_data || {};
      const grossAmountValue = parseFloat(grossAmount);
      const ssEmployee = taxDetail?.federal_taxes?.social_security?.employee || 0;
      const medicareEmployee = taxDetail?.federal_taxes?.medicare?.employee || 0; // Updated
      const medicareEmployer = taxDetail?.federal_taxes?.medicare?.employer || 0;
      const federalIncomeTax = taxDetail?.federal_taxes?.federal_income_tax || 0;
      const stateIncomeTax = taxDetail?.state_taxes?.state_income_tax || 0;
      const futaTax = taxDetail?.federal_taxes?.futa_tax || 0;
      const ssEmployer = taxDetail?.federal_taxes?.social_security?.employer || 0;
      const stateUnemployment = taxDetail?.state_taxes?.state_unemployment_tax || 0;
      const localIncomeTax = taxDetail?.local_taxes?.local_income_tax || 0;
      const mctdSurcharge = taxDetail?.local_taxes?.mctdSurcharge || 0;
      const nyPaidFamilyLeave = taxDetail?.local_taxes?.nycTax.nyPaidFamilyLeave || 0;
      const netWeeklyPaidValue = taxDetail?.net_pay || 0;
      const nyStateDisabilityInsurance =
        taxDetail?.local_taxes?.nycTax?.nyStateDisabilityInsurance || 0;
      const fica = (grossAmountValue * ssEmployee).toFixed(2);
      const medicare = medicareEmployee.toFixed(2); // Medicare deduction value
      const withHoldingTax = (grossAmountValue * federalIncomeTax).toFixed(2);
      const stateWithHoldingTax = '0.00';
      const others = employee.other_deductions || '0.00';
      const federalIncomeTaxDeduction = federalIncomeTax;
      const ssEmployeeTax = ssEmployee;
      const ssEmployerTax = ssEmployer;
      const stateIncomeTaxDeduction = stateIncomeTax;
      const stateUnemploymentTaxAmount = stateUnemployment;
      const localIncomeTaxDeduction = localIncomeTax || '0.00';
      const healthInsurance = employee.health_insurance || '0.00';
      const retirement = employee.retirement_contributions || '0.00';
      const healthSavingsAccount = employee.health_saving_account || '0.00';
      const qualifiedTransportation = employee.qualified_transportation || '0.00';
      const groupLifeInsurance = employee.group_life_insurance || '0.00';
      // AFTER TAX DEDUCTIONS
      const roth401k = taxDetail?.after_tax_deductions?.roth401k || 0;
      const additionalInsurance = taxDetail?.after_tax_deductions?.additional_insurance || 0;
      const unionDues = taxDetail?.after_tax_deductions?.union_dues || 0;
      const charitableContributions =
        taxDetail?.after_tax_deductions?.charitable_contributions || 0;
      const loanRepayments = taxDetail?.after_tax_deductions?.loan_repayments || 0;
      // Calculate total deductions

      const totalEmployerLiability = (
        parseFloat(ssEmployerTax) +
        parseFloat(futaTax) +
        parseFloat(stateUnemploymentTaxAmount)
      ).toFixed(2);

      const totalDeductions = (
        parseFloat(federalIncomeTaxDeduction) + // Added federal income tax
        parseFloat(ssEmployeeTax) + // Added social security employee tax
        parseFloat(medicare) + // Added medicare employee tax
        parseFloat(medicareEmployer) + // Added medicare employer tax
        parseFloat(futaTax) + // Added FUTA tax
        parseFloat(ssEmployerTax) + // Added social security employer tax
        parseFloat(healthInsurance) + // Added health insurance
        parseFloat(retirement) + // Added retirement plan
        parseFloat(stateIncomeTaxDeduction) + // Added state income tax
        parseFloat(stateUnemploymentTaxAmount) + // Added state unemployment tax
        parseFloat(localIncomeTaxDeduction) + // Added local income tax
        // parseFloat(totalEmployerLiability) +    // Added total employer liability
        parseFloat(mctdSurcharge) + // Added MCTD surcharge
        parseFloat(nyPaidFamilyLeave) + // Added state disability family leave
        parseFloat(nyStateDisabilityInsurance) + // Added state disability insurance
        parseFloat(roth401k) + // Added roth 401k
        parseFloat(additionalInsurance) + // Added additional insurance
        parseFloat(unionDues) + // Added union dues
        parseFloat(loanRepayments) + // Added loan repayments
        parseFloat(charitableContributions) + // Added charitable contributions
        parseFloat(healthSavingsAccount) + // Added health savings account
        parseFloat(qualifiedTransportation) + // Added qualified transportation
        parseFloat(groupLifeInsurance)
      ) // Added group life insurance
        .toFixed(2);

      const netWeeklyPaid = netWeeklyPaidValue;
      const final_net_pay = (netWeeklyPaid).toFixed(2);
      console.log('🚀 ~ netWeeklyPaid:', netWeeklyPaid, 'final_net_pay', final_net_pay);
      setNetWeeklyPaid(netWeeklyPaid);
      setTotalDeductions(totalDeductions);
      return {
        final_net_pay,
        netWeeklyPaid,
        deductions: {
          federalIncomeTax: `$${federalIncomeTaxDeduction}`,
          ssEmployee: `$${ssEmployeeTax}`,
          medicareEmployee: `$${medicare}`, // Updated
          additionalMedicareTax: `$${medicare}`, // Updated
          medicareEmployer: `$${medicareEmployer}`, // Updated
          withHoldingTax: `$${withHoldingTax}`,
          stateWithHoldingTax: `$${stateWithHoldingTax}`,
          futaTax: `$${futaTax}`,
          ssEmployer: `$${ssEmployerTax}`,
          healthInsurance: `$${healthInsurance}`,
          retirementPlan: `$${retirement}`,
          miscOthers: `$${others}`,
          stateIncomeTax: `$${stateIncomeTaxDeduction}`,
          stateUnemploymentTax: `$${stateUnemploymentTaxAmount}`,
          localIncomeTax: `$${localIncomeTaxDeduction}`,
          totalEmployerLiability: `$${totalEmployerLiability}`,
          mctdSurcharge: `$${mctdSurcharge}`,
          stateDisabilityFamilyLeave: `$${nyPaidFamilyLeave}`,
          stateDisabilityInsurance: `$${nyStateDisabilityInsurance}`,
          roth401k: `$${roth401k}`,
          additionalInsurance: `$${additionalInsurance}`,
          unionDues: `$${unionDues}`,
          loanRepayments: `$${loanRepayments}`,
          charitableContributions: `$${charitableContributions}`,
          healthSavingsAccount: `$${healthSavingsAccount}`,
          qualifiedTransportation: `$${qualifiedTransportation}`,
          groupLifeInsurance: `$${groupLifeInsurance}`,
        },
      };
    },
    [employeeTaxData]
  );

  const transformEmployeeData = useCallback(
    (apiData) => {
      if (!apiData || !apiData.length) return [];
      return apiData.map((employee, index) => {
        const payrollNumber = String(index + 1).padStart(3, '0');
        const { standardHours, overtimeHours } = calculateHoursFromTimesheet(employee);
        const grossAmount = (
          parseFloat(standardHours.totalEarned) + parseFloat(overtimeHours.totalEarned)
        ).toFixed(2);
        const taxDetails = employeeTaxData
          ? getEmployeeTaxDetails(employee, grossAmount)
          : {
              final_net_pay: 0, // Default estimate
              deductions: {
                federalIncomeTax: '$0.00',
                ssEmployee: '$0.00',
                medicare: '$0.00',
                additioanlmedicareEmployee: '$0.00',
                withHoldingTax: '$0.00',
                stateWithHoldingTax: '$0.00',
                others: '$0.00',
                futaTax: '$0.00',
                ssEmployer: '$0.00',
                healthInsurance: '$0.00',
                retirement: '$0.00',
                stateIncomeTax: '$0.00',
                stateUnemployment: '$0.00',
                localIncomeTax: '$0.00',
                totalEmployerLiability: '$0.00',
                mctdSurcharge: '$0.00',
                nyPaidFamilyLeave: '$0.00',
                nyStateDisabilityInsurance: '$0.00',
              },
            };
        const updatedEmployee = {
          id: employee.id,
          payrollNumber,
          name: `${employee.first_name} ${employee.last_name}`,
          secondName: employee.trade_worker_title_id || 'General Worker',
          withHolding: employee.number_of_deductions || '0',
          grossAmount: `$${grossAmount}`,
          netWeeklyPaid: `$${taxDetails?.final_net_pay}`,
          standardHours,
          overtimeHours,
          deductions: taxDetails.deductions,
          apiData: employee,
        };

        return updatedEmployee;
      });
    },
    [calculateHoursFromTimesheet, employeeTaxData, getEmployeeTaxDetails]
  );
  const employeeData = useMemo(
    () => transformEmployeeData(rawPayrollData),
    [rawPayrollData, transformEmployeeData]
  );

  const paginatedEmployees = useMemo(() => {
    if (employeeData?.length === 0) return [];
    return employeeData?.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  }, [employeeData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleNavigateBack = () => {
    navigate('/accounts-management/payroll/genrate-payroll');
  };

  const extractEmployeeTaxDetails = useCallback((employee) => {
    const apiData = employee.apiData || {};
    const ssn =
      apiData.ssn ||
      apiData.social_security_number ||
      `${Math.floor(1000 + Math.random() * 9000)} XXX-XX-${Math.floor(
        1000 + Math.random() * 9000
      )}`;

    const address = apiData.address || apiData.employee_address || {};
    const street =
      typeof address === 'string'
        ? address
        : address.street || apiData.street_address || 'Not Available';
    const city = address.city || apiData.city || 'Not Available';
    const state = address.state || apiData.state || 'NY';
    const zipCode =
      address.zip_code || address.zipCode || apiData.zip_code || apiData.postal_code || '10001';
    const filingStatus = apiData.filing_status || apiData.tax_filing_status || 'Single';
    const w4Info = apiData.w4_information || apiData.w4_info || {};
    const allowances = w4Info.allowances || {};
    const dependents = parseInt(allowances.dependents || apiData.number_of_deductions || 0);
    const otherAllowances = parseInt(allowances.other_allowances || apiData.other_allowances || 0);
    const additionalWithholding = parseFloat(
      w4Info.additional_withholding || apiData.additional_withholding || 0
    );
    const otherIncome = parseFloat(w4Info.other_income || apiData.other_income || 0);
    const deductions = parseFloat(w4Info.deductions || apiData.deductions || 0);
    const healthInsurance = parseFloat(apiData.health_insurance || 0);
    const health_savings_account= parseFloat(apiData.health_saving_account || 0);
    const group_life_insurance = parseFloat(apiData.group_life_insurance || 0);
    const qualified_transportation = parseFloat(apiData.qualified_transportation || 0);
    const retirementContributions = parseFloat(apiData.retirement_contributions || 0);
    const otherPreTaxDeductions = parseFloat(apiData.other_deductions || 0);
    const companyCar = parseFloat(
      apiData.company_car || apiData.taxable_fringe_benefits?.company_car || 0
    );
    const healthInsuranceSCorp = parseFloat(apiData.health_insurance_s_corp || 0);
    const otherBenefits = parseFloat(apiData.other_taxable_benefits || 0);
    const stateTaxInfo = apiData.state_tax_info || {};
    const stateUnemploymentTaxRate = parseFloat(stateTaxInfo.state_unemployment_tax_rate || 0);
    const stateTaxWithholdingMethod =
      stateTaxInfo.state_tax_withholding_method ||
      apiData.state_tax_withholding_method ||
      'percentage';

    const afterTaxDeductions = {
      roth401k: parseFloat(apiData.roth_401_k || 0),
      additionalInsurance: parseFloat(apiData.additional_insurance || 0),
      unionDues: parseFloat(apiData.union_dues || 0),
      charitableContributions: parseFloat(apiData.charitable_contributions || 0),
      loanRepayments: parseFloat(apiData.loan_repayments || 0),
    };
    return {
      fullName: `${apiData.first_name || ''} ${apiData.last_name || ''}`.trim(),
      ssn,
      address: {
        street,
        city,
        state,
        zipCode,
      },
      filingStatus,
      stateOfResidence: state,
      localTaxInfo: {
        city,
        localTaxCode: apiData.local_tax_code || '',
      },
      w4Information: {
        allowances: {
          dependents,
          otherAllowances,
        },
        additionalWithholding,
        otherIncome,
        deductions,
      },
      preTaxDeductions: {
        healthInsurance,
        qualified_transportation,
        group_life_insurance,
        health_savings_account,
        retirementContributions,
        other: otherPreTaxDeductions,
      },
      taxableFringeBenefits: {
        companyCar,
        healthInsuranceSCorp,
        other: otherBenefits,
      },
      afterTaxDeductions,
      stateTaxInfo: {
        stateUnemploymentTaxRate,
        stateTaxWithholdingMethod,
      },
    };
  }, []);

  const sendEmployeeTaxData = useCallback(
    async (employeeData) => {
      if (!employeeData || employeeData.length === 0) return;
      const currentEmployee = employeeData[0];
      const employeeId = currentEmployee.id;
      const cacheKey = `${employeeId}-${weekEndingsDate}`;
      if (employeeTaxApiProcessed[cacheKey]) return;
      try {
        setLoadingTaxData(true);
        setLoading(true);
        const taxDetails = extractEmployeeTaxDetails(currentEmployee);
        const weekEndDate = weekEndingsDate ? new Date(weekEndingsDate) : new Date();
        const weekStartDate = new Date(weekEndDate);
        weekStartDate.setDate(weekStartDate.getDate() - 6);
        const formatDate = (date) =>
          `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
        const grossAmount = parseFloat(currentEmployee.grossAmount.replace('$', '')) || 0;

        const taxRequestData = {
          employee_information: {
            employee_details: {
              full_name: taxDetails.fullName,
              ssn: taxDetails.ssn,
              address: {
                street: taxDetails.address.street,
                city: taxDetails.address.city,
                state: taxDetails.address.state,
                zip_code: taxDetails.address.zipCode,
              },
              filing_status: taxDetails.filingStatus,
              state_of_residence: taxDetails.stateOfResidence,
              local_tax_info: {
                city: taxDetails.localTaxInfo.city,
                local_tax_code: taxDetails.localTaxInfo.localTaxCode,
              },
              w4_information: {
                allowances: {
                  dependents: taxDetails.w4Information.allowances.dependents,
                  other_allowances: taxDetails.w4Information.allowances.otherAllowances,
                },
                additional_withholding: taxDetails.w4Information.additionalWithholding,
                other_income: taxDetails.w4Information.otherIncome,
                deductions: taxDetails.w4Information.deductions,
              },
            },
            payroll_information: {
              pay_period_start: formatDate(weekStartDate),
              pay_period_end: formatDate(weekEndDate),
              pay_period: 'weekly',
              gross_wages: grossAmount,
              other_taxable_compensation: 0,
              pre_tax_deductions: {
                health_insurance: taxDetails.preTaxDeductions.healthInsurance,
                health_savings_account: taxDetails.preTaxDeductions.health_savings_account,
                qualified_transportation: taxDetails.preTaxDeductions.qualified_transportation,
                group_life_insurance: taxDetails.preTaxDeductions.group_life_insurance,
                retirement_contributions: taxDetails.preTaxDeductions.retirementContributions,
                other: taxDetails.preTaxDeductions.other,
              },
              taxable_fringe_benefits: {
                company_car: taxDetails.taxableFringeBenefits.companyCar,
                health_insurance_s_corp: taxDetails.taxableFringeBenefits.healthInsuranceSCorp,
                other: taxDetails.taxableFringeBenefits.other,
              },
              after_tax_deductions: {
                roth401k: taxDetails.afterTaxDeductions.roth401k,
                additional_insurance: taxDetails.afterTaxDeductions.additionalInsurance,
                union_dues: taxDetails.afterTaxDeductions.unionDues,
                charitable_contributions: taxDetails.afterTaxDeductions.charitableContributions,
                loan_repayments: taxDetails.afterTaxDeductions.loanRepayments,
              },
              state_tax_info: {
                state_unemployment_tax_rate: taxDetails.stateTaxInfo.stateUnemploymentTaxRate,
                state_tax_withholding_method: taxDetails.stateTaxInfo.stateTaxWithholdingMethod,
              },
            },
          },
        };
        console.log('🚀 ~ sendEmployeeTaxData ~ taxRequestData:', taxRequestData);
        const response = await axios.post(
          'https://tax.ezelogs.com/generate_tax_info',
          taxRequestData
        );
        if (response && response.data) {
          setEmployeeTaxApiProcessed((prev) => ({
            ...prev,
            [cacheKey]: true,
          }));
          setEmployeeTaxData(response.data);
          fetchPayrollData(response.data);
        } else {
          console.error('Failed to generate tax information:', response);
          setError('Failed to generate tax information. Please try again.');
          setLoading(false); // Make sure to set loading to false here
        }
      } catch (error) {
        console.error('Error generating tax information:', error);
        setError(`Failed to generate tax information: ${error.message}`);
      } finally {
        setLoadingTaxData(false);
        setLoading(false); // Make sure to set loading to false here
      }
    },
    [weekEndingsDate, extractEmployeeTaxDetails, employeeTaxApiProcessed]
  );
  useEffect(() => {
    if (employeeData.length > 0) {
      sendEmployeeTaxData(employeeData);
    }
  }, [employeeData, sendEmployeeTaxData]);

  const fetchPayrollData = useCallback(
    async (taxData = null) => {
      if (!weekEndingsDate) return;
      const formattedDate = new Date(weekEndingsDate).toLocaleDateString('en-CA');
      try {
        setError(null);
        const response = await getRequest(
          `${endpoints.accounting.payroll.generate_payroll.get_payroll_weekly_timesheet}?date=${formattedDate}`
        );
        let payloadData = [];
        if (response && response.metadata && response.metadata.success) {
          payloadData = response.payload || [];
        } else if (response && response.payload) {
          payloadData = response.payload;
        } else if (Array.isArray(response)) {
          payloadData = response;
        } else {
          setError('Invalid data format received from server');
          setRawPayrollData([]);
          return;
        }
        if (taxData) {
          setEmployeeTaxData(taxData);
          setRawPayrollData(payloadData);
        } else {
          setRawPayrollData(payloadData);
        }
      } catch (err) {
        console.error('Error fetching payroll data:', err);
        setError(`Failed to fetch payroll data: ${err.message}`);
        setRawPayrollData([]);
      } finally {
      }
    },
    [weekEndingsDate]
  );
  useEffect(() => {
    if (weekEndingsDate) {
      fetchPayrollData();
    }
  }, [weekEndingsDate, fetchPayrollData]);
  const handleIssuePayment = useCallback(
    async (employeeId) => {
      try {
        setLoading(true);
        const employee = employeeData.find((emp) => emp.id === employeeId);
        if (!employee) {
          throw new Error('Employee not found');
        }
        const payrollSubmission = [
          {
            employee_id: employee.id,
            standard_hours: employee.standardHours.totalHours,
            overtime_hours: employee.overtimeHours.totalHours,
            standard_rate: employee.standardHours.payRate,
            overtime_rate: employee.overtimeHours.payRate,
            gross_amount: employee.grossAmount.replace('$', ''),
            deductions: employee.deductions.totalDeductions.replace('$', ''),
            net_amount: employee.netWeeklyPaid.replace('$', ''),
            week_ending: weekEndingsDate
              ? new Date(weekEndingsDate).toISOString().split('T')[0]
              : null,
          },
        ];

        const formData = new FormData();
        formData.append('payroll_data', JSON.stringify(payrollSubmission));
        formData.append(
          'week_ending',
          weekEndingsDate ? new Date(weekEndingsDate).toISOString().split('T')[0] : null
        );
        formData.append('single_payment', 'true');
        formData.append('payroll_number', employee.payrollNumber);

        await postRequest(
          `${endpoints.accounting.payroll.generate_payroll.create_payroll}`,
          formData,
          true
        );
        fetchPayrollData();

        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 3000);
      } catch (err) {
        console.error('Error issuing payment:', err);
        setError(`Failed to issue payment: ${err.message}`);
        setTimeout(() => setError(null), 5000);
      } finally {
      }
    },
    [employeeData, weekEndingsDate, fetchPayrollData]
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);
      setError(null);

      if (employeeData.length === 0) {
        throw new Error('No employee data available to save');
      }

      const totalGrossAmount = employeeData
        .reduce((sum, employee) => {
          return sum + parseFloat(employee.grossAmount.replace('$', ''));
        }, 0)
        .toFixed(2);

      const totalWeeklyPaidWages = employeeData
        .reduce((sum, employee) => {
          return sum + parseFloat(employee.netWeeklyPaid.replace('$', ''));
        }, 0)
        .toFixed(2);

      const weekEndingDate = new Date(weekEndingsDate);
      weekEndingDate.setHours(0, 0, 0, 0);
      const weekStartDate = new Date(weekEndingDate);
      weekStartDate.setDate(weekStartDate.getDate() - 6);

      const formatDate = (date) => {
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
          .getDate()
          .toString()
          .padStart(2, '0')}`;
      };

      const payrollPeriod = `${formatDate(weekStartDate)} to ${formatDate(weekEndingDate)}`;

      const formData = new FormData();
      formData.append('week_ending', weekEndingsDate ? formatDate(weekEndingDate) : null);
      formData.append('week_ending_date', formatDate(weekEndingDate)); 
      formData.append('total_gross_amount', totalGrossAmount);
      formData.append('total_weekly_paid_wages', totalWeeklyPaidWages);
      formData.append('payroll_number', staticPayrollNumber); 
      formData.append('payroll_period', payrollPeriod);
      formData.append('payroll_date', formatDate(weekEndingsDate));
      formData.append('payment_method', 'direct_deposit');

      employeeData.forEach((employee) => {
        formData.append('employee_ids[]', employee.id.toString());
      });

      // Send the payroll data to the server
      await postRequest(
        `${endpoints.accounting.payroll.generate_payroll.create_payroll}`,
        formData,
        true
      );
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
      reset();
      navigate('/accounts-management/payroll/genrate-payroll');
    } catch (err) {
      console.error('Error saving payroll:', err);
      setError(`Failed to save payroll: ${err.message}`);
      setLoading(false);
    }
  });
  return (
    <>
      <div style={{ marginLeft: '14px' }}>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <PayrollHeader
            handleNavigateBack={handleNavigateBack}
            error={error}
            submitSuccess={submitSuccess}
            companyInfo={companyInfo}
            control={control}
            setWeekEndingsDate={setWeekEndingsDate}
          />

          {loading && (
            <Card sx={{ mb: 3, mx: 2 }}>
              <CardContent
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
              >
                <CircularProgress sx={{ mr: 2 }} />
                <Typography>Calculating employee taxes using AI. Please wait — do not refresh....</Typography>
              </CardContent>
            </Card>
          )}
          {!loading && paginatedEmployees.length > 0
            ? paginatedEmployees.map((employee) => (
                <PayrollEmployeeCard
                  key={employee.id}
                  employee={employee}
                  loading={loading}
                  handleIssuePayment={handleIssuePayment}
                  bankInformation={bankInformation}
                  employees={employeeData}
                  netWeeklyPaid={netWeeklyPaid}
                  totalDeductions={totalDeductions}
                  selectedDate={formatDate(weekEndingsDate)} 
                  saveTrigger={saveTrigger} 
                />
              ))
            : null}
          {employeeData.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <Pagination
                count={Math.ceil(employeeData.length / rowsPerPage)}
                page={page}
                onChange={handleChangePage}
                shape="rounded"
                color="primary"
              />
            </Box>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '16px',
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              className="dark-button"
              onClick={() => setSaveTrigger(prev => !prev)}
            >
              Save
            </LoadingButton>
          </div>
        </FormProvider>
      </div>
    </>
  );
};
export default GeneratePayrollCreatePage;